<main>
  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'Costs_Reports_Title' | translateWF: 'Cost Reports' }}</h1>
      </div>
    </div>

    <p-panel [header]="'Costs_Reports_Production' | translateWF: 'Production'" [toggleable]="true">
      <!-- Daily Production -->
      <section class="report-section">
        <div class="report-header flex justify-between items-start">
          <div>
            <h3>{{ 'Costs_Reports_Daily_Production' | translateWF: 'Daily Production' }}</h3>
            <p class="report-description">
              {{ 'Costs_Reports_Daily_Production_Description' | translateWF: 'Production transactions by area with the cost each one produced' }}
            </p>
          </div>
          <p-button
            [label]="'Costs_Reports_Generate_Report' | translateWF: 'Generate Report'"
            icon="pi pi-download"
            (onClick)="generateDailyProductionReport()"
            [outlined]="true"
            [loading]="loadingDailyProduction()"
            [disabled]="!dailyProductionForm.valid || isAnyReportLoading()"
          />
        </div>
        <form [formGroup]="dailyProductionForm" class="report-form">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 mt-6">
            <div class="form-box">
              <label for="daily-production-from">{{ 'Costs_Reports_From_Date' | translateWF: 'From Date' }} *</label>
              <p-datepicker id="daily-production-from" formControlName="fromDate" [iconDisplay]="'input'" [showIcon]="true" [showTime]="true" fluid class="w-full" />
              <small class="p-error" *ngIf="dailyProductionForm.get('fromDate')?.invalid && dailyProductionForm.get('fromDate')?.touched">
                {{ 'Costs_Reports_From_Date_Required' | translateWF: 'From date is required' }}
              </small>
            </div>

            <div class="form-box">
              <label for="daily-production-to">{{ 'Costs_Reports_To_Date' | translateWF: 'To Date' }} *</label>
              <p-datepicker id="daily-production-to" formControlName="toDate" [iconDisplay]="'input'" [showIcon]="true" [showTime]="true" fluid class="w-full" />
              <small class="p-error" *ngIf="dailyProductionForm.get('toDate')?.invalid && dailyProductionForm.get('toDate')?.touched">
                {{ 'Costs_Reports_To_Date_Required' | translateWF: 'To date is required' }}
              </small>
            </div>

            <div class="form-box lg:col-span-2">
              <label for="daily-production-processes">{{ 'Costs_Reports_Processes' | translateWF: 'Processes' }} *</label>
              <p-multiSelect
                id="daily-production-processes"
                formControlName="processes"
                [options]="processOptions"
                optionLabel="label"
                optionValue="value"
                display="chip"
                fluid
                class="w-full"
              />
              <small class="p-error" *ngIf="dailyProductionForm.get('processes')?.invalid && dailyProductionForm.get('processes')?.touched">
                {{ 'Costs_Reports_Processes_Required' | translateWF: 'Select at least one process' }}
              </small>
            </div>
          </div>
        </form>
      </section>
    </p-panel>

    <p-panel [header]="'Costs_Reports_Reconciliation' | translateWF: 'Reconciliation'" [toggleable]="true">
      <!-- Cost key reconciliation -->
      <section class="report-section">
        <div class="report-header flex justify-between items-start">
          <div>
            <h3>{{ 'Costs_Reports_Cost_Key_Reconciliation' | translateWF: 'Cost Key Reconciliation' }}</h3>
            <p class="report-description">
              {{ 'Costs_Reports_Reconciliation_Description' | translateWF: 'Inputs of a cost key for the selected month and how they were charged' }}
            </p>
          </div>
          <p-button
            [label]="'Costs_Reports_Generate_Report' | translateWF: 'Generate Report'"
            icon="pi pi-download"
            (onClick)="generateReconciliationReport()"
            [outlined]="true"
            [loading]="loadingReconciliation()"
            [disabled]="!reconciliationForm.valid || isAnyReportLoading()"
          />
        </div>
        <form [formGroup]="reconciliationForm" class="report-form">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 mt-6">
            <div class="form-box">
              <label for="reconciliation-year">{{ 'Year' | translateWF: 'Year' }} *</label>
              <p-select
                id="reconciliation-year"
                formControlName="year"
                [options]="yearOptions"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'Costs_Reports_Select_Year' | translateWF: 'Select year'"
                fluid
                class="w-full"
              />
              <small class="p-error" *ngIf="reconciliationForm.get('year')?.invalid && reconciliationForm.get('year')?.touched">
                {{ 'Costs_Reports_Year_Required' | translateWF: 'Year is required' }}
              </small>
            </div>

            <div class="form-box">
              <label for="reconciliation-month">{{ 'Month' | translateWF: 'Month' }} *</label>
              <p-select
                id="reconciliation-month"
                formControlName="month"
                [options]="monthOptions"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'Costs_Reports_Select_Month' | translateWF: 'Select month'"
                fluid
                class="w-full"
              >
                <ng-template let-option pTemplate="item">
                  <span>{{ option.key | translateWF: option.label }}</span>
                </ng-template>
                <ng-template let-option pTemplate="selectedItem">
                  <span>{{ option.key | translateWF: option.label }}</span>
                </ng-template>
              </p-select>
              <small class="p-error" *ngIf="reconciliationForm.get('month')?.invalid && reconciliationForm.get('month')?.touched">
                {{ 'Costs_Reports_Month_Required' | translateWF: 'Month is required' }}
              </small>
            </div>

            <div class="form-box">
              <label for="reconciliation-cost-key">{{ 'Costs_Reports_Cost_Key' | translateWF: 'Cost Key' }} *</label>
              <p-select
                id="reconciliation-cost-key"
                formControlName="costKey"
                [options]="reconciliationOptions"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'Costs_Reports_Select_Cost_Key' | translateWF: 'Select a cost key'"
                fluid
                class="w-full"
              />
              <small class="p-error" *ngIf="reconciliationForm.get('costKey')?.invalid && reconciliationForm.get('costKey')?.touched">
                {{ 'Costs_Reports_Cost_Key_Required' | translateWF: 'Cost key is required' }}
              </small>
            </div>
          </div>
        </form>
      </section>
    </p-panel>
  </div>
</main>
